import React, { useEffect, useState } from "react";
import { getStoredUserInfo, setStoredUserInfo } from "../../utils/storage";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
} from "@mui/material";
import { REGENERATE_TOKEN, GET_PROJECTS_FOR_USER } from "../../ApiEndpoints";
import UserLanguageSelector from "../UserLanguageSelector";

const ROLE_NAMES = {
    transcriber: "Transcriber",
    translator: "Translator",
    performancedirection: "Performance Direction",
    lipqualityassurance: "Lips Quality Check",
    speakerfaceidentification: "Speaker Face Identification",
    qualityassurance: "Quality Assurance",
    performancedirectionversions: "QA Review",
  };

const prettifyRole = (code = "") =>
    ROLE_NAMES[code.toLowerCase()] ||
    code
        .replace(/[_-]/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const UserProfile = () => {
    const [userInfo, setUserInfo] = useState(null);
    const [projects, setProjects] = useState([]);
    const [tokenRows, setTokenRows] = useState(1);
    const [fluentLanguages, setFluentLanguages] = useState([]);

    useEffect(() => {
        const storedUser = getStoredUserInfo();
        if (storedUser) {
            const parsed = storedUser;
            setUserInfo(parsed);
            setTokenRows(Math.ceil(((parsed.token ?? "").length) / 80));

            fetchProjects(parsed);
        }
    }, []);

    const fetchProjects = async (user) => {
        try {
            const res = await fetch(GET_PROJECTS_FOR_USER, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify({ userId: user.id }),
            });

            const data = await res.json();
            if (data.projects) {
                setProjects(data.projects);
            }
        } catch (error) {
            console.error("Failed to fetch projects:", error);
        }
    };

    const handleRegenerate = async () => {
        const res = await fetch(REGENERATE_TOKEN, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`,
            },
            body: JSON.stringify({
                clientId: userInfo.id,
                plan: "pro",
                features: ["voice", "translation"],
            }),
        });

        const data = await res.json();
        if (data.token) {
            const updatedUser = { ...userInfo, token: data.token };
            setStoredUserInfo(updatedUser);
            setUserInfo(updatedUser);
            setTokenRows(Math.ceil(((data.token ?? "").length) / 80));
        }
    };

    if (!userInfo) return null;

    return (
        <Grid item container justifyContent="center">
            <Grid item container xs={7} marginTop={7} direction="column">
                <Paper
                    elevation={4}
                    sx={{
                        p: 3,
                        mx: "auto",
                        width: {
                            xs: "90%",
                            sm: "80%",
                            md: "70%",
                            lg: "60%",
                        },
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Name
                    </Typography>
                    <TextField
                        fullWidth
                        value={userInfo.name}
                        InputProps={{ readOnly: true }}
                    />

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Email Address
                    </Typography>
                    <TextField
                        fullWidth
                        value={userInfo.email}
                        InputProps={{ readOnly: true }}
                    />

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Roles:
                    </Typography>
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            paddingX: 2,
                            paddingY: 1,
                            backgroundColor: "#f9f9f9",
                            fontFamily: "monospace",
                            maxHeight: 110,
                            overflowY: "auto",
                        }}
                    >
                        <ul style={{ paddingLeft: 16, margin: 0 }}>
                            <li><strong>{userInfo.isAdmin ? "Admin" : "User"}</strong></li>
                            {projects.map((proj) => (
                                <li key={`${proj.id}-${proj.role}`}>
                                    <strong>{proj.name}</strong> ({prettifyRole(proj.role)})
                                </li>
                            ))}
                        </ul>
                    </Box>


                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        API Key
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1}>
                        <TextField
                            fullWidth
                            multiline
                            rows={tokenRows}
                            value={userInfo.token}
                            InputProps={{ readOnly: true }}
                            sx={{
                                backgroundColor: "#f5f5f5",
                                fontFamily: "monospace",
                                overflow: "hidden",
                            }}
                        />

                        <Button onClick={handleRegenerate} color="primary" variant="outlined">
                            Regenerate
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <UserLanguageSelector
                            selectedLanguages={fluentLanguages}
                            onChange={setFluentLanguages}
                        />
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default UserProfile;
